import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

const getUserLabel = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
  user?.name ||
  user?.email ||
  `User #${user?.id}`;

const resolveDefaultRecipientIds = (users, context = {}) => {
  const ids = [];
  const caseOfficerId = context?.caseOfficerId;
  if (caseOfficerId != null) {
    const match = users.find((u) => Number(u?.id) === Number(caseOfficerId));
    if (match?.id != null) ids.push(match.id);
  }
  if (ids.length > 0) return ids;

  const caseOfficerName = String(context?.caseOfficer || "").trim();
  if (!caseOfficerName || caseOfficerName.toUpperCase() === "N/A") return ids;

  const normalized = caseOfficerName.toLowerCase();
  const byName = users.find((u) => {
    const label = getUserLabel(u).toLowerCase();
    const name = String(u?.name || "").toLowerCase();
    return label === normalized || name === normalized;
  });
  if (byName?.id != null) ids.push(byName.id);
  return ids;
};

const SendNotificationModal = ({ open, onClose, users = [], context = {}, onSent }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedIds(resolveDefaultRecipientIds(users, context));
    setMessage(context?.defaultMessage || "");
    setSearch("");
  }, [open, users, context?.caseOfficerId, context?.caseOfficer, context?.defaultMessage]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      `${getUserLabel(user)} ${user?.email || ""}`.toLowerCase().includes(term)
    );
  }, [users, search]);

  const toggleUser = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.includes(u.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIdSet = new Set(filteredUsers.map((u) => u.id));
      setSelectedIds((prev) => prev.filter((id) => !filteredIdSet.has(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...filteredUsers.map((u) => u.id)])]);
    }
  };

  const { mutate: sendNotification, isLoading: sending } = useMutation({
    mutationFn: () =>
      axios.post(
        `${API_URL}/notification`,
        {
          recipientIds: selectedIds,
          message: message.trim(),
          clientId: context?.clientId ?? null,
          clientAccountDetailId: context?.clientAccountDetailId ?? context?.n ?? null,
          admissionId: context?.admissionId ?? null,
          admissionTermId: context?.admissionTermId ?? null,
          termNumber: context?.termNumber ?? null,
          module: context?.module ?? "account",
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      ),
    onSuccess: () => {
      toast.success("Notification sent");
      onSent?.();
      onClose?.();
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data?.errorMessage ||
          "Failed to send notification"
      ),
  });

  const canSend = selectedIds.length > 0 && message.trim().length > 0 && !sending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send Notification to Admin</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" className="mb-2">
          Recipients{selectedIds.length > 0 ? ` (${selectedIds.length} selected)` : ""}
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className="mt-2 border border-app-border rounded max-h-[240px] overflow-y-auto">
          {filteredUsers.length > 0 ? (
            <List dense disablePadding>
              <ListItemButton onClick={toggleSelectAll} divider>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={allFilteredSelected}
                  indeterminate={
                    !allFilteredSelected && filteredUsers.some((u) => selectedIds.includes(u.id))
                  }
                  disableRipple
                />
                <ListItemText primary="Select all" />
              </ListItemButton>
              {filteredUsers.map((user) => (
                <ListItemButton key={user.id} onClick={() => toggleUser(user.id)}>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={selectedIds.includes(user.id)}
                    disableRipple
                  />
                  <ListItemText
                    primary={getUserLabel(user)}
                    secondary={[user?.email, user?.type].filter(Boolean).join(" · ")}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2" className="p-4 text-center text-app-text-muted">
              No users found
            </Typography>
          )}
        </div>

        <Divider className="my-3" />

        <TextField
          label="Message"
          placeholder="Write a message for the selected users..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => sendNotification()} disabled={!canSend}>
          {sending ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendNotificationModal;
